/**
 * Reusable input-formatting helpers for fields whose on-screen DISPLAY value must differ from the
 * CANONICAL value persisted/sent to the API (Admin Sign Up QA correction pass). Every helper here is a
 * pure function: a component keeps the CANONICAL value in its own state (digits only, no punctuation) and
 * derives the DISPLAY value from it on every render — `<input value={formatX(canonical)} onChange={e =>
 * setCanonical(digitsFromX(e.target.value))} />`. Because `onChange` always re-derives the canonical value
 * from whatever raw string the input currently holds, typing one digit at a time, pasting a formatted or
 * unformatted value, and backspacing all converge on the same result — there is exactly one code path, not
 * one per interaction type. The backend performs its own authoritative validation independently; these
 * helpers only shape what the user sees and what gets sent, never replace server-side validation.
 */

/** Strips every non-digit character and caps the result at `maxDigits` — the shared building block for
 * every digit-based canonical value below (FEIN, phone, ZIP, routing number). */
function digitsOnly(raw: string, maxDigits: number): string {
  return raw.replace(/\D/g, '').slice(0, maxDigits);
}

// ---- FEIN — canonical: up to 9 digits, no punctuation. Display: XX-XXXXXXX. ----

export function feinDigitsFromInput(raw: string): string {
  return digitsOnly(raw, 9);
}

export function formatFeinDisplay(digits: string): string {
  const d = digitsOnly(digits, 9);
  if (d.length <= 2) return d;
  return `${d.slice(0, 2)}-${d.slice(2)}`;
}

// ---- US phone (Company Phone, Office Number, Owner/invite Phone) — canonical: up to 10 digits.
// Display: (XXX) XXX-XXXX. Intentionally US-only — do not apply to a field whose domain is international. ----

export function usPhoneDigitsFromInput(raw: string): string {
  return digitsOnly(raw, 10);
}

export function formatUsPhoneDisplay(digits: string): string {
  const d = digitsOnly(digits, 10);
  if (d.length === 0) return '';
  if (d.length < 4) return `(${d}`;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

// ---- ZIP code (Billing/Mailing) — canonical: up to 9 digits. Display: XXXXX or XXXXX-XXXX. ----

export function zipDigitsFromInput(raw: string): string {
  return digitsOnly(raw, 9);
}

export function formatZipDisplay(digits: string): string {
  const d = digitsOnly(digits, 9);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}

// ---- Bank routing number — canonical AND display are identical (digits only, no mask): the approved
// design has no decorative mask for this field, and masking it would make QA unable to confirm the value
// actually entered. Kept here only for the shared max-length/digit-stripping behavior. ----

export function routingNumberDigitsFromInput(raw: string): string {
  return digitsOnly(raw, 9);
}

// ---- Bank account number — canonical AND display are identical (digits only, no mask), same rationale
// as the routing number. The 4-17 digit cap mirrors PaymentProcessing.Api's own authoritative validation
// (FundingAccountService.CreateForOnboardingAsync) exactly — this is not a client-invented length; it only
// prevents typing past what the backend would reject anyway, and never truncates a value the backend would
// otherwise accept. ----

export function accountNumberDigitsFromInput(raw: string): string {
  return digitsOnly(raw, 17);
}

/**
 * Website — normalizes a bare hostname (e.g. "company.com") to "https://company.com" on blur/submit.
 * Deterministic protocol normalization only: does not touch a value that already has a scheme (including
 * "http://", which is left as the user entered it), does not add "www.", and does not validate the
 * hostname itself — that remains the existing/backend validation's job. An empty/whitespace-only value is
 * returned unchanged so an optional field can stay empty.
 */
export function normalizeWebsiteUrl(value: string): string {
  const trimmed = value.trim();
  if (trimmed === '' || /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
}
